import type { CSSProperties, ReactNode } from "react";
import { Check, ChevronRight } from "lucide-react";
import { cn } from "@/shared/utils/cn";
import { HighlightedText } from "./HighlightedText";
import { RowSkeleton } from "./RowSkeleton";

interface InfoRowProps {
  title: ReactNode;
  topCaption?: ReactNode;
  subtitle?: ReactNode;
  caption?: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  actions?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  href?: string;
  ariaLabel?: string;
  bold?: boolean;
  searchText?: string;
  density?: "compact" | "comfortable";
  isLoading?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function InfoRow({
  title,
  topCaption,
  subtitle,
  caption,
  icon,
  trailing,
  actions,
  selected,
  onClick,
  href,
  ariaLabel,
  bold = false,
  searchText = "",
  density = "comfortable",
  isLoading = false,
  className,
  style,
}: InfoRowProps) {
  if (isLoading) return <RowSkeleton className={className} />;

  const content = (
    <>
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="min-w-0 flex-1 space-y-0.5">
        {topCaption && <span className="block truncate text-xs text-muted-foreground">{topCaption}</span>}
        <span className={cn("block text-[15px] leading-snug [overflow-wrap:anywhere]", bold ? "font-bold" : "font-semibold")}>
          {typeof title === "string" ? <HighlightedText text={title} search={searchText} /> : title}
        </span>
        {subtitle && <span className="block truncate text-xs leading-relaxed text-muted-foreground">{subtitle}</span>}
        {caption && <span className="block text-xs leading-relaxed text-muted-foreground">{caption}</span>}
      </span>
      {selected && (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="size-4" aria-hidden="true" />
          <span className="sr-only">Selected</span>
        </span>
      )}
      {trailing && <span className="flex max-w-[42%] shrink-0 flex-wrap items-center justify-end gap-1">{trailing}</span>}
      {(href || onClick) && selected === undefined && !trailing && !actions && <ChevronRight className="size-[18px] shrink-0" aria-hidden="true" />}
    </>
  );
  const contentClasses = cn(
    "flex min-h-11 min-w-0 flex-1 self-stretch items-center gap-3 rounded-[inherit] px-3 text-left",
    density === "compact" ? "py-2.5" : "min-h-[82px] py-3 sm:px-[18px] sm:py-3.5",
  );

  return (
    <div className={cn("flex min-w-0 items-center rounded-2xl border border-border bg-card text-card-foreground transition-colors", selected && "border-primary bg-matcha", className)} style={style}>
      {href ? (
        <a
          href={href}
          onClick={onClick}
          aria-label={ariaLabel}
          className={cn(contentClasses, "hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring")}
        >
          {content}
        </a>
      ) : onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-pressed={selected}
          aria-label={ariaLabel}
          className={cn(contentClasses, "cursor-pointer bg-transparent hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring")}
        >
          {content}
        </button>
      ) : (
        <div className={contentClasses}>{content}</div>
      )}
      {actions && <div className="flex max-w-[42%] shrink-0 flex-wrap items-center justify-end gap-1 pr-3">{actions}</div>}
    </div>
  );
}
